import { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderOpen, Download, Loader2, Lock, Crown, CheckCircle2 } from "lucide-react";

export function InstallToGame() {
  const ctx = useApp();
  const [gamePath, setGamePath] = useState("C:\\Program Files\\Hytale\\worlds");
  const [installing, setInstalling] = useState(false);
  const [done, setDone] = useState(false);

  const isGated = ctx.isFeatureGated("ai");

  const handleInstall = async () => {
    if (!ctx.currentWorld || isGated) return;
    setInstalling(true);
    setDone(false);

    try {
      const axios = (await import("axios")).default;
      const { API } = await import("@/config");

      // Get all export formats
      const [hytaleRes, prefabRes, jarRes] = await Promise.all([
        axios.get(`${API}/worlds/${ctx.currentWorld.id}/export/hytale`),
        axios.get(`${API}/worlds/${ctx.currentWorld.id}/export/prefab`),
        axios.get(`${API}/worlds/${ctx.currentWorld.id}/export/jar`),
      ]);

      // Create ZIP using JSZip
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      const worldName = ctx.currentWorld.name.replace(/\s+/g, "_");

      // Add Hytale config
      zip.file(`${worldName}_hytale.json`, JSON.stringify(hytaleRes.data, null, 2));

      // Add Prefab
      const prefabData = prefabRes.data.data || prefabRes.data;
      zip.file(`${worldName}.prefab.json`, JSON.stringify(prefabData, null, 2));

      // Add JAR (base64 encoded)
      if (jarRes.data.data_base64) {
        const binaryString = atob(jarRes.data.data_base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        zip.file(`${worldName}_worldgen.jar`, bytes);
      }

      // Add README
      const readme = `=== ${ctx.currentWorld.name} — Installation Guide ===

Generated by Hyt Orbis World Builder
https://hytorbisworldbuilder.com

INSTALL PATH: ${gamePath}

INSTRUCTIONS:
1. Extract all files from this ZIP
2. Copy "${worldName}_hytale.json" to:
   ${gamePath}/config/
3. Copy "${worldName}.prefab.json" to:
   ${gamePath}/prefabs/
4. Copy "${worldName}_worldgen.jar" to:
   ${gamePath}/mods/
5. Restart your game

WORLD INFO:
- Name: ${ctx.currentWorld.name}
- Map Size: ${ctx.currentWorld.map_size || "64x64"}
- Zones: ${(ctx.currentWorld.zones || []).length}
- Prefabs: ${(ctx.currentWorld.prefabs || []).length}
- Seed: ${ctx.currentWorld.seed || "random"}

Need help? Visit https://hytorbisworldbuilder.com
`;
      zip.file("README.txt", readme);

      // Download ZIP
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${worldName}_install_package.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setDone(true);
    } catch (e) {
      console.error("Install package failed:", e);
      alert("Failed to create install package");
    }
    setInstalling(false);
  };

  return (
    <Dialog open={ctx.showInstallDialog} onOpenChange={ctx.setShowInstallDialog}>
      <DialogContent className="install-dialog" data-testid="install-dialog">
        <DialogHeader>
          <DialogTitle className="install-dialog-title">
            <FolderOpen size={20} /> Install to Game
          </DialogTitle>
          <DialogDescription className="install-dialog-desc">
            Download a ready-to-install package for your world
          </DialogDescription>
        </DialogHeader>

        {isGated ? (
          <div className="install-gated" data-testid="install-gated">
            <Lock size={32} />
            <h3>Premium Feature</h3>
            <p>Upgrade to Creator or Developer to export install packages for your game.</p>
            <Button onClick={() => { ctx.setShowInstallDialog(false); ctx.setShowPricingDialog(true); }} data-testid="install-upgrade-btn">
              <Crown size={16} /> View Plans
            </Button>
          </div>
        ) : !ctx.currentWorld ? (
          <div className="install-gated">
            <p>Select a world first to create an install package.</p>
          </div>
        ) : done ? (
          <div className="install-done" data-testid="install-done">
            <CheckCircle2 size={40} />
            <h3>Package Downloaded!</h3>
            <p>Extract the ZIP and follow the README.txt instructions to install <strong>{ctx.currentWorld.name}</strong> into your game.</p>
            <Button variant="outline" onClick={() => setDone(false)}>Create Another</Button>
          </div>
        ) : (
          <div className="install-form" data-testid="install-form">
            <div className="install-world-info">
              <strong>{ctx.currentWorld.name}</strong>
              <span>{ctx.currentWorld.map_size || "64x64"} — {(ctx.currentWorld.zones || []).length} zones, {(ctx.currentWorld.prefabs || []).length} prefabs</span>
            </div>

            <div className="form-group">
              <Label>Game Install Path</Label>
              <Input
                value={gamePath}
                onChange={(e) => setGamePath(e.target.value)}
                placeholder="C:\Program Files\Hytale\worlds"
                data-testid="install-path-input"
              />
              <span className="install-path-hint">This path will be included in the install instructions</span>
            </div>

            <div className="install-contents">
              <span className="install-contents-title">Package includes:</span>
              <ul>
                <li>Hytale world config (.json)</li>
                <li>Prefab data (.prefab.json)</li>
                <li>World generation mod (.jar)</li>
                <li>Installation instructions (README.txt)</li>
              </ul>
            </div>

            <Button
              onClick={handleInstall}
              disabled={installing}
              className="install-btn"
              data-testid="install-download-btn"
            >
              {installing ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
              {installing ? "Creating Package..." : "Download Install Package"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
